import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Image, Zap, CheckCircle, AlertTriangle,
  Edit3, Trash2, ChevronRight, RotateCcw, Calendar, Clock,
  MapPin, ArrowLeft, Plus, X, Info,
} from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import toast from 'react-hot-toast';

// ── Design tokens (matches Game Day Zen Sporty system) ───────────────────────
const T = {
  bg:      '#060a0f',
  s1:      'rgba(255,255,255,0.04)',
  s2:      'rgba(255,255,255,0.07)',
  s3:      'rgba(255,255,255,0.10)',
  border:  'var(--border)',
  text:    'var(--text)',
  text2:   'var(--text-2)',
  ice:     'var(--ice)',
  iceDim:  'var(--ice-a8)',
  iceBdr:  'var(--ice-a20)',
  goal:    '#00e87a',
  penalty: '#ff3b3b',
  puck:    '#ff8c1a',
  zen:     '#7dd3b8',
};

const ALLOWED_TYPES = {
  'image/jpeg':    { label: 'JPEG',    icon: <Image size={16} /> },
  'image/png':     { label: 'PNG',     icon: <Image size={16} /> },
  'image/webp':    { label: 'WebP',    icon: <Image size={16} /> },
  'image/gif':     { label: 'GIF',     icon: <Image size={16} /> },
  'application/pdf': { label: 'PDF',   icon: <FileText size={16} /> },
};

// ── Step labels ───────────────────────────────────────────────────────────────
const STEPS = ['Upload', 'Analyzing', 'Review', 'Done'];

function StepBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 40 }}>
      {STEPS.map((label, i) => {
        const active   = i === step;
        const complete = i < step;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, fontFamily: "'Barlow Condensed', sans-serif",
                background: complete ? T.goal : active ? T.ice : T.s2,
                color: complete || active ? '#000' : T.text2,
                border: `2px solid ${complete ? T.goal : active ? T.ice : T.border}`,
                transition: 'all 0.3s',
                boxShadow: active ? `0 0 16px ${T.iceBdr}` : 'none',
              }}>
                {complete ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: active ? T.ice : complete ? T.goal : T.text2,
                whiteSpace: 'nowrap',
              }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, margin: '-18px 8px 0',
                background: complete ? T.goal : T.border,
                transition: 'background 0.3s',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 1: Upload ────────────────────────────────────────────────────────────
function UploadStep({ onFile, fileInfo, preview }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const processFile = useCallback((file) => {
    if (!file) return;
    if (!ALLOWED_TYPES[file.type]) {
      toast.error(`Unsupported file type. Please upload a JPEG, PNG, WebP, GIF, or PDF.`);
      return;
    }
    if (file.size > 14 * 1024 * 1024) {
      toast.error('File is too large. Maximum is 14 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const base64 = dataUrl.split(',')[1];
      // Notify parent with both the send payload AND the display metadata
      onFile({
        payload:  { filename: file.name, mimeType: file.type, data: base64, fileSize: file.size },
        fileInfo: { name: file.name, size: file.size, type: file.type },
        preview:  file.type.startsWith('image/') ? dataUrl : null,
      });
    };
    reader.readAsDataURL(file);
  }, [onFile]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? T.ice : fileInfo ? T.goal : T.border}`,
          borderRadius: 16,
          padding: '48px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? T.iceDim : fileInfo ? 'rgba(0,232,122,0.04)' : T.s1,
          transition: 'all 0.2s',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif,.pdf"
          style={{ display: 'none' }}
          onChange={(e) => processFile(e.target.files[0])}
        />

        {/* Background glow when dragging */}
        {dragging && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse at center, var(--ice-a7) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
        )}

        {fileInfo ? (
          <div style={{ position: 'relative', zIndex: 1 }}>
            {preview ? (
              <img
                src={preview}
                alt="Schedule preview"
                style={{
                  maxWidth: '100%', maxHeight: 260, borderRadius: 10,
                  margin: '0 auto 20px', display: 'block',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}
              />
            ) : (
              <div style={{
                width: 72, height: 72, borderRadius: 16,
                background: 'rgba(0,232,122,0.1)',
                border: '1px solid rgba(0,232,122,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <FileText size={32} style={{ color: T.goal }} />
              </div>
            )}
            <p style={{ fontWeight: 700, color: T.text, marginBottom: 4, fontSize: 15 }}>
              {fileInfo.name}
            </p>
            <p style={{ color: T.text2, fontSize: 13 }}>
              {ALLOWED_TYPES[fileInfo.type]?.label} · {formatSize(fileInfo.size)}
            </p>
            <p style={{ color: T.goal, fontSize: 13, marginTop: 8, fontWeight: 600 }}>
              ✓ Ready — click Analyze to extract schedule
            </p>
          </div>
        ) : (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 16,
              background: T.iceDim,
              border: `1px solid ${T.iceBdr}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <Upload size={32} style={{ color: T.ice }} />
            </div>
            <p style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 8,
              fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              Drop your schedule here
            </p>
            <p style={{ color: T.text2, fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>
              Drag & drop or click to browse
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {Object.entries(ALLOWED_TYPES).map(([type, info]) => (
                <span key={type} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 20,
                  background: T.s2, border: `1px solid ${T.border}`,
                  fontSize: 11, color: T.text2, fontWeight: 600,
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>
                  {info.icon} {info.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info box */}
      <div style={{
        marginTop: 20, padding: '14px 18px',
        background: T.s1, borderRadius: 12,
        border: `1px solid ${T.border}`,
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}>
        <Info size={16} style={{ color: T.ice, flexShrink: 0, marginTop: 1 }} />
        <p style={{ color: T.text2, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          Upload a screenshot or PDF of your arena's ice-time booking confirmation.
          The AI will extract every ice slot — you'll review and edit them before anything is saved.
          <strong style={{ color: T.text }}> PDF support requires Gemini as your AI provider.</strong>
        </p>
      </div>
    </div>
  );
}

// ── Step 2: Analyzing ─────────────────────────────────────────────────────────
function AnalyzingStep({ filename }) {
  // Provider is not known until the server responds — show "AI" generically here.
  // The real provider name is shown in the Review step from the server response.
  return (
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      {/* Pulsing puck */}
      <div style={{ position: 'relative', display: 'inline-block', marginBottom: 32 }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: T.iceDim, border: `2px solid ${T.iceBdr}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'spin 2s linear infinite',
        }}>
          <Zap size={36} style={{ color: T.ice }} />
        </div>
        <div style={{
          position: 'absolute', inset: -8, borderRadius: '50%',
          border: `2px solid ${T.iceBdr}`,
          animation: 'ping 1.5s ease-out infinite',
          opacity: 0.6,
        }} />
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes ping { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.6); opacity: 0; } }
      `}</style>

      <h2 style={{
        fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
        fontSize: 26, fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: T.text, marginBottom: 10,
      }}>
        Extracting Schedule
      </h2>
      <p style={{ color: T.text2, fontSize: 14, marginBottom: 6 }}>
        AI is reading <strong style={{ color: T.text }}>{filename}</strong>
      </p>
      <p style={{ color: T.text2, fontSize: 13 }}>
        This usually takes 5–15 seconds…
      </p>
    </div>
  );
}

// ── Step 3: Review ────────────────────────────────────────────────────────────
// Apr 30 2026 — exported so the new-team wizard (DashboardPage) can render
// the same review UI inline instead of bouncing the coach to a fresh
// /games/import page that loses the extracted games. The component is
// self-contained: takes games + provider + filename + onConfirm + onReset
// callbacks, manages its own internal edit state.
// 2026-07-10 (Phase 5.1) — optional totalCostCents/costCurrency props: when
// the AI found a permit total on the document, the summary bar shows it and
// notes it will be recorded to the team budget on import. Both props are
// optional so existing callers (incl. the DashboardPage wizard) keep working.
export function ReviewStep({ games: initialGames, provider, filename, onConfirm, onReset, totalCostCents = null, costCurrency = null }) {
  const [games, setGames]     = useState(() => initialGames.map((g, i) => ({ ...g, _id: i, _selected: true })));
  const [editing, setEditing] = useState(null); // _id of row being edited
  const [submitting, setSubmitting] = useState(false);

  const providerLabel = { anthropic: 'Claude', openai: 'GPT-4o', gemini: 'Gemini' }[provider] || provider;
  const selected = games.filter(g => g._selected);

  const update = (id, patch) => setGames(gs => gs.map(g => g._id === id ? { ...g, ...patch } : g));
  const remove = (id) => setGames(gs => gs.filter(g => g._id !== id));

  const addRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    const newGame = { date: today, startTime: '20:00', endTime: '21:30', arenaName: '', notes: null, _id: Date.now(), _selected: true };
    setGames(gs => [...gs, newGame]);
    setEditing(newGame._id);
  };

  const toggleAll = () => {
    const allSelected = games.every(g => g._selected);
    setGames(gs => gs.map(g => ({ ...g, _selected: !allSelected })));
  };

  const handleConfirm = async () => {
    if (selected.length === 0) { toast.error('Select at least one game to import'); return; }
    setSubmitting(true);
    try {
      const payload = selected.map(({ date, startTime, endTime, arenaName, notes }) =>
        ({ date, startTime, endTime, arenaName, notes })
      );
      await onConfirm(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    try {
      return new Date(d.slice(0,10) + 'T12:00:00').toLocaleDateString('en-CA', {
        weekday: 'short', month: 'short', day: 'numeric',
      });
    } catch { return d; }
  };

  const inputStyle = {
    background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8,
    color: T.text, fontSize: 13, padding: '6px 10px', width: '100%', outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div>
      {/* Summary bar */}
      <div style={{
        padding: '14px 18px', borderRadius: 12,
        background: 'rgba(0,232,122,0.06)',
        border: '1px solid rgba(0,232,122,0.15)',
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
        flexWrap: 'wrap',
      }}>
        <CheckCircle size={18} style={{ color: T.goal, flexShrink: 0 }} />
        <p style={{ color: T.text, fontSize: 14, margin: 0, flex: 1 }}>
          <strong style={{ color: T.goal }}>{providerLabel}</strong> found{' '}
          <strong>{games.length} ice time{games.length !== 1 ? 's' : ''}</strong> in{' '}
          <em style={{ color: T.text2 }}>{filename}</em>.
          Review and adjust below, then import.
        </p>
        <span style={{ color: T.text2, fontSize: 13 }}>
          <strong style={{ color: T.ice }}>{selected.length}</strong> of {games.length} selected
        </span>
        {/* 5.1 — permit total, when the document printed one */}
        {Number(totalCostCents) > 0 && (
          <span style={{ width: '100%', fontSize: 13, color: T.text2, marginTop: 2 }}>
            💰 Permit total found: <strong style={{ color: T.goal }}>
              ${(totalCostCents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {costCurrency ? ` ${costCurrency.toUpperCase()}` : ''}
            </strong> — will be recorded as an Ice/Rink Rental expense in the team budget.
          </span>
        )}
      </div>

      {/* Table
          2026-07-14 (mobile pass) — the grid below is 6 fixed/1fr columns
          that total well over 390px (40+100+100+120=360px of fixed columns
          alone, before the two 1fr date/arena columns get any width). On a
          phone this clipped columns off-screen with no way to reach them.
          Wrapping in an overflow-x:auto scroller + min-width on the inner
          content lets it scroll horizontally instead of clipping, while
          leaving desktop (which already had room) pixel-identical since the
          wrapper only engages when content exceeds the viewport. */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 20 }}>
      <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, minWidth: 640 }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 100px 100px 1fr 120px',
          gap: 0,
          background: T.s2,
          borderBottom: `1px solid ${T.border}`,
          padding: '10px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input type="checkbox"
              checked={games.length > 0 && games.every(g => g._selected)}
              onChange={toggleAll}
              style={{ cursor: 'pointer', accentColor: T.ice }}
            />
          </div>
          {['Date', 'Start', 'End', 'Arena / Rink', 'Actions'].map((h, i) => (
            <div key={i} style={{
              fontSize: 11, fontWeight: 700, color: T.text2,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: i === 4 ? '0 0 0 8px' : '0',
            }}>
              {h}
            </div>
          ))}
        </div>

        {/* Rows */}
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {games.map((game, idx) => {
            const isEditing = editing === game._id;
            const rowBg = !game._selected ? 'rgba(0,0,0,0)' : idx % 2 === 0 ? T.s1 : 'transparent';

            return (
              <div key={game._id} style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 100px 100px 1fr 120px',
                gap: 0,
                padding: '10px 16px',
                background: rowBg,
                borderBottom: `1px solid ${T.border}`,
                opacity: game._selected ? 1 : 0.4,
                transition: 'opacity 0.15s',
                alignItems: 'center',
              }}>
                {/* Checkbox */}
                <input type="checkbox"
                  checked={game._selected}
                  onChange={() => update(game._id, { _selected: !game._selected })}
                  style={{ cursor: 'pointer', accentColor: T.ice }}
                />

                {/* Date */}
                <div>
                  {isEditing ? (
                    <input type="date" value={game.date}
                      onChange={e => update(game._id, { date: e.target.value })}
                      style={inputStyle}
                    />
                  ) : (
                    <div>
                      <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                        {formatDate(game.date)}
                      </div>
                      {game.notes && (
                        <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>
                          {game.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Start */}
                <div>
                  {isEditing ? (
                    <input type="time" value={game.startTime}
                      onChange={e => update(game._id, { startTime: e.target.value })}
                      style={inputStyle}
                    />
                  ) : (
                    <span style={{ fontSize: 13, color: T.ice, fontWeight: 600 }}>
                      {game.startTime}
                    </span>
                  )}
                </div>

                {/* End */}
                <div>
                  {isEditing ? (
                    <input type="time" value={game.endTime || ''}
                      onChange={e => update(game._id, { endTime: e.target.value || null })}
                      style={inputStyle}
                    />
                  ) : (
                    <span style={{ fontSize: 13, color: game.endTime ? T.text2 : T.border }}>
                      {game.endTime || '—'}
                    </span>
                  )}
                </div>

                {/* Arena */}
                <div>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="text"
                        placeholder="Arena name"
                        value={game.arenaName}
                        onChange={e => update(game._id, { arenaName: e.target.value })}
                        style={inputStyle}
                      />
                      <input
                        type="text"
                        placeholder="Notes (optional)"
                        value={game.notes || ''}
                        onChange={e => update(game._id, { notes: e.target.value || null })}
                        style={inputStyle}
                      />
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 13, color: T.text }}>
                        {game.arenaName || <span style={{ color: T.border }}>—</span>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, paddingLeft: 8 }}>
                  {isEditing ? (
                    <button
                      onClick={() => setEditing(null)}
                      style={{
                        padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        background: T.goal, color: '#000', border: 'none', cursor: 'pointer',
                      }}
                    >Done</button>
                  ) : (
                    <button
                      onClick={() => setEditing(game._id)}
                      style={{
                        padding: 6, borderRadius: 8,
                        background: T.s2, border: `1px solid ${T.border}`,
                        color: T.text2, cursor: 'pointer',
                      }}
                      title="Edit"
                    >
                      <Edit3 size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => { remove(game._id); if (editing === game._id) setEditing(null); }}
                    style={{
                      padding: 6, borderRadius: 8,
                      background: 'rgba(255,59,59,0.08)', border: '1px solid rgba(255,59,59,0.15)',
                      color: T.penalty, cursor: 'pointer',
                    }}
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* Add row + actions */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        <button onClick={addRow} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: T.s2, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer',
        }}>
          <Plus size={14} /> Add row
        </button>
        <button onClick={onReset} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: T.s1, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer',
        }}>
          <RotateCcw size={14} /> Start over
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={handleConfirm}
          disabled={submitting || selected.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700,
            background: selected.length === 0 ? T.s2 : T.ice,
            color: selected.length === 0 ? T.text2 : '#000',
            border: 'none', cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
            fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
            letterSpacing: '0.08em', textTransform: 'uppercase',
            opacity: submitting ? 0.7 : 1, transition: 'all 0.2s',
          }}
        >
          {submitting ? (
            <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Saving…</>
          ) : (
            <><CheckCircle size={16} /> Import {selected.length} game{selected.length !== 1 ? 's' : ''}</>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Done ──────────────────────────────────────────────────────────────
function DoneStep({ result, onGoToSchedule, onImportMore }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'rgba(0,232,122,0.12)', border: '2px solid rgba(0,232,122,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 28px',
        boxShadow: '0 0 32px rgba(0,232,122,0.15)',
      }}>
        <CheckCircle size={40} style={{ color: T.goal }} />
      </div>

      <h2 style={{
        fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
        fontSize: 30, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: T.text, marginBottom: 12,
      }}>
        Schedule Imported!
      </h2>

      <div style={{
        display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 32, flexWrap: 'wrap',
      }}>
        <div style={{
          padding: '20px 32px', borderRadius: 14,
          background: 'rgba(0,232,122,0.08)', border: '1px solid rgba(0,232,122,0.2)',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: 48, fontWeight: 800, color: T.goal,
            fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
            lineHeight: 1,
          }}>{result.created}</div>
          <div style={{ fontSize: 12, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>
            Games Added
          </div>
        </div>
        {result.skipped > 0 && (
          <div style={{
            padding: '20px 32px', borderRadius: 14,
            background: T.s1, border: `1px solid ${T.border}`,
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 48, fontWeight: 800, color: T.text2,
              fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
              lineHeight: 1,
            }}>{result.skipped}</div>
            <div style={{ fontSize: 12, color: T.text2, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>
              Already Existed
            </div>
          </div>
        )}
      </div>

      <p style={{ color: T.text2, fontSize: 14, marginBottom: 32 }}>
        Your games are now on the schedule and players will be notified when you send reminders.
      </p>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button onClick={onImportMore} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 22px', borderRadius: 12, fontSize: 14, fontWeight: 600,
          background: T.s2, border: `1px solid ${T.border}`, color: T.text2, cursor: 'pointer',
        }}>
          <Upload size={15} /> Import Another
        </button>
        <button onClick={onGoToSchedule} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 22px', borderRadius: 12, fontSize: 14, fontWeight: 700,
          background: T.ice, color: '#000', border: 'none', cursor: 'pointer',
          fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          <Calendar size={15} /> View Schedule <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────
const ERROR_HINTS = {
  'No AI API key':     { title: 'AI Not Configured', hint: 'Go to Admin → AI Settings and save a Gemini or Claude API key.' },
  'fetch failed':      { title: 'Could Not Reach AI Provider', hint: 'The server could not connect to the AI API. Check that your API key is valid in Admin → AI Settings.' },
  'timed out':         { title: 'AI Request Timed Out', hint: 'The document took too long to process. Try uploading a screenshot of just the booking table instead of the full PDF.' },
  'timeout':           { title: 'Request Timed Out', hint: 'The AI took too long to respond. Try a screenshot of just the schedule table instead of the full PDF.' },
  'Network Error':     { title: 'Connection Error', hint: 'Check your internet connection and try again.' },
  'vision supports':   { title: 'Unsupported File Type', hint: 'For PDFs, set your AI provider to Gemini in Admin → AI Settings. Otherwise upload a screenshot (JPEG/PNG).' },
  'No valid ice-time': { title: 'No Ice Times Found', hint: 'The AI could not find any ice-time bookings. Try a clearer screenshot or crop to just the schedule table.' },
  'JSON array':        { title: 'AI Parse Error', hint: 'The AI returned an unexpected response. Try again — this is usually temporary.' },
};

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  const match = Object.entries(ERROR_HINTS).find(([k]) => message.includes(k));
  const title = match ? match[1].title : 'Import Failed';
  const hint  = match ? match[1].hint  : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 14,
      padding: '16px 20px', borderRadius: 14, marginBottom: 24,
      background: 'rgba(255,59,59,0.10)',
      border: '1.5px solid rgba(255,59,59,0.35)',
      boxShadow: '0 2px 12px rgba(255,59,59,0.08)',
    }}>
      <AlertTriangle size={20} style={{ color: '#ff3b3b', flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>
          {title}
        </p>
        <p style={{ color: 'var(--text)', fontSize: 13, margin: hint ? '0 0 6px' : 0, opacity: 0.85, wordBreak: 'break-word' }}>
          {message}
        </p>
        {hint && (
          <p style={{ color: 'var(--text-2)', fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            💡 {hint}
          </p>
        )}
      </div>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: 'none', border: 'none', color: 'var(--text-2)',
          cursor: 'pointer', padding: 2, flexShrink: 0,
        }}>
          <X size={16} />
        </button>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ImportSchedulePage() {
  const { teamId } = useParams();
  const navigate   = useNavigate();
  const { isCoach, loading: roleLoading } = useTeamRole(teamId);

  const [step,         setStep]        = useState(0);
  const [filePayload,  setFilePayload]  = useState(null); // what we send to the API
  const [fileInfo,     setFileInfo]     = useState(null); // display metadata (name/size/type)
  const [preview,      setPreview]      = useState(null); // data-URL for image preview
  const [extracted,    setExtracted]    = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error,        setError]        = useState(null);

  // Season assignment (optional — import can be unlinked)
  const [seasons,      setSeasons]      = useState([]);
  const [importSeasonId, setImportSeasonId] = useState('');
  const [newSeasonName, setNewSeasonName]   = useState('');
  const [creatingSeasonInline, setCreatingSeasonInline] = useState(false);

  useEffect(() => {
    api.get(`/teams/${teamId}/seasons`)
      .then(r => setSeasons(r.data))
      .catch(() => {});
  }, [teamId]);

  // Redirect non-coaches
  if (!roleLoading && !isCoach) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <AlertTriangle size={48} style={{ color: T.penalty, margin: '0 auto 16px' }} />
        <h2 style={{ color: T.text }}>Coaches Only</h2>
        <p style={{ color: T.text2 }}>Schedule import is only available to coaches and managers.</p>
        <button onClick={() => navigate(`/team/${teamId}/games`)} style={{ marginTop: 20, padding: '10px 24px', background: T.ice, color: '#000', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700 }}>
          Back to Schedule
        </button>
      </div>
    );
  }

  const handleFileSelected = ({ payload, fileInfo: fi, preview: pv }) => {
    setFilePayload(payload);
    setFileInfo(fi);
    setPreview(pv);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!filePayload) { toast.error('Please select a file first'); return; }
    setStep(1);
    setError(null);

    try {
      const { data } = await api.post(`/teams/${teamId}/import-schedule/extract`, filePayload, {
        timeout: 90_000, // 90s — PDF analysis can take 30-60s for multi-page permits
      });
      setExtracted(data);
      setStep(2);
    } catch (err) {
      let msg = err.response?.data?.error || err.message || 'Extraction failed. Please try again.';
      // Normalize raw network errors into a readable message
      if (msg === 'fetch failed' || msg === 'Failed to fetch') {
        msg = 'fetch failed';  // handled by ERROR_HINTS lookup in ErrorBanner
      }
      setError(msg);
      setStep(0);
    }
  };

  const handleConfirm = async (games) => {
    try {
      const { data } = await api.post(`/teams/${teamId}/import-schedule/confirm`, {
        games,
        seasonId: importSeasonId || undefined,
        // 5.2 — pass the extracted permit total back so the backend can
        // record it as a season-scoped budget expense (undefined = none found).
        totalCostCents: extracted?.totalCostCents || undefined,
        costCurrency: extracted?.costCurrency || undefined,
      });
      setImportResult(data);
      setStep(3);
      toast.success(`🏟 ${data.created} game${data.created !== 1 ? 's' : ''} added to schedule!`);
      if (data.costRecorded) toast.success('💰 Permit cost recorded in the team budget');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed. Please try again.');
      throw err;
    }
  };

  const handleReset = () => {
    setStep(0);
    setFilePayload(null);
    setFileInfo(null);
    setPreview(null);
    setExtracted(null);
    setError(null);
  };

  return (
    <div style={{ padding: '32px 24px', maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 40 }}>
        <button
          onClick={() => navigate(`/team/${teamId}/games`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            background: T.s1, border: `1px solid ${T.border}`, color: T.text2,
            cursor: 'pointer', flexShrink: 0, marginTop: 4,
          }}
        >
          <ArrowLeft size={15} /> Schedule
        </button>
        <div>
          <h1 style={{
            fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
            fontSize: 36, fontWeight: 800, letterSpacing: '0.04em',
            textTransform: 'uppercase', color: T.text, margin: 0, lineHeight: 1,
          }}>
            <span style={{ color: T.ice }}>Import</span> Schedule
          </h1>
          <p style={{ color: T.text2, fontSize: 14, marginTop: 6 }}>
            Upload a PDF or screenshot from your arena — AI extracts all ice times for review.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <StepBar step={step} />

      {/* Error banner */}
      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {/* Step content */}
      <div style={{
        background: T.s1, border: `1px solid ${T.border}`,
        borderRadius: 20, padding: 32,
      }}>
        {step === 0 && (
          <>
            <UploadStep onFile={handleFileSelected} fileInfo={fileInfo} preview={preview} />

            {/* Season assignment — shown always so coach can pick before or after upload */}
            <div style={{ marginTop: 24, padding: '16px', borderRadius: 14, background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
                📅 Assign to Season <span style={{ fontWeight: 400, color: 'var(--text-2)', fontSize: 12 }}>(optional)</span>
              </p>
              {seasons.length > 0 ? (
                <>
                  <select
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 10, fontSize: 13,
                      background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)',
                      marginBottom: 8 }}
                    value={importSeasonId}
                    onChange={e => { setImportSeasonId(e.target.value); setCreatingSeasonInline(false); }}>
                    <option value="">— No season (import to All Games) —</option>
                    {/* Apr 2026 — only active seasons offered for
                        import. Importing into an archived season
                        would defeat the point of archiving. */}
                    {seasons.filter(s => s.isActive !== false).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => { setCreatingSeasonInline(v => !v); setImportSeasonId(''); }}
                    style={{ fontSize: 12, color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    + Create a new season instead
                  </button>
                </>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>
                  No seasons yet. Create one to organise your import:
                </p>
              )}

              {/* Inline new season form
                  2026-07-16 (mobile pass) — flexWrap + minWidth:0 on the
                  text input. With nested padding (page 24px + step card
                  32px + season box 16px = 72px/side) a 390px phone only
                  has ~246px here; a flex:1 <input> won't shrink below its
                  browser-default intrinsic width (~170-190px) without
                  minWidth:0, so the Create button could get squeezed off
                  to the right. flexWrap lets it drop to its own line
                  instead of clipping; harmless on desktop where it fits. */}
              {(creatingSeasonInline || seasons.length === 0) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    placeholder='e.g. "Summer 2025 Sunday Hockey"'
                    maxLength={120}
                    value={newSeasonName}
                    onChange={e => setNewSeasonName(e.target.value)}
                    style={{ flex: 1, minWidth: 0, padding: '9px 12px', borderRadius: 10, fontSize: 13,
                      background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  />
                  <button
                    disabled={!newSeasonName.trim()}
                    onClick={async () => {
                      if (!newSeasonName.trim()) return;
                      try {
                        const { data } = await api.post(`/teams/${teamId}/seasons`, { name: newSeasonName.trim() });
                        setSeasons(prev => [data, ...prev]);
                        setImportSeasonId(data.id);
                        setNewSeasonName('');
                        setCreatingSeasonInline(false);
                        toast.success(`Season "${data.name}" created!`);
                      } catch { toast.error('Failed to create season'); }
                    }}
                    style={{ padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      background: newSeasonName.trim() ? 'var(--ice)' : 'var(--surface-3)',
                      color: newSeasonName.trim() ? '#000' : 'var(--text-2)',
                      border: 'none', cursor: newSeasonName.trim() ? 'pointer' : 'default' }}>
                    Create
                  </button>
                </div>
              )}
              {importSeasonId && (
                <p style={{ fontSize: 12, color: 'var(--goal)', marginTop: 8, fontWeight: 600 }}>
                  ✓ Games will be imported into "{seasons.find(s => s.id === importSeasonId)?.name}"
                </p>
              )}
            </div>

            {filePayload && (
              <div style={{ marginTop: 24 }}>
                <button
                  onClick={handleAnalyze}
                  style={{
                    width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 16, fontWeight: 700,
                    background: T.ice, color: '#000', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    boxShadow: `0 4px 24px var(--ice-a20)`,
                    transition: 'all 0.2s',
                  }}
                >
                  <Zap size={18} />
                  Analyze with AI
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}

        {step === 1 && (
          <AnalyzingStep filename={fileInfo?.name || 'file'} />
        )}

        {step === 2 && extracted && (
          <ReviewStep
            games={extracted.games}
            provider={extracted.provider}
            filename={extracted.filename || fileInfo?.name || 'file'}
            onConfirm={handleConfirm}
            onReset={handleReset}
            totalCostCents={extracted.totalCostCents}
            costCurrency={extracted.costCurrency}
          />
        )}

        {step === 3 && importResult && (
          <DoneStep
            result={importResult}
            onGoToSchedule={() => navigate(`/team/${teamId}/games`)}
            onImportMore={handleReset}
          />
        )}
      </div>

      {/* Bottom hint */}
      {step < 2 && (
        <p style={{ color: T.border, fontSize: 12, textAlign: 'center', marginTop: 20 }}>
          Files are sent directly to the AI and never stored on our servers.
        </p>
      )}
    </div>
  );
}
